import { MessageType } from "./subscription.model";
import { User } from './user.model';

export enum OrganizationType {
    CORPORATE = 'CORPORATE',
    EDUCATIONAL = 'EDUCATIONAL',
    NON_PROFIT = 'NON_PROFIT',
    GOVERNMENT = 'GOVERNMENT'
}

export enum OrganizationStatus {
    ACTIVE = 'ACTIVE',
    INACTIVE = 'INACTIVE',
    SUSPENDED = 'SUSPENDED',
    PENDING = 'PENDING'
}

export enum OrganizationUserRole {
    ADMIN = 'ADMIN',
    MANAGER = 'MANAGER',
    MEMBER = 'MEMBER'
}

export enum OrganizationUserStatus {
    ACTIVE = 'ACTIVE',
    INACTIVE = 'INACTIVE',
    PENDING = 'PENDING'
}

export enum BillingCycle {
    MONTHLY = 'MONTHLY',
    QUARTERLY = 'QUARTERLY',
    ANNUAL = 'ANNUAL'
}

export enum OrganizationBillingStatus {
    PENDING = 'PENDING',
    PAID = 'PAID',
    FAILED = 'FAILED'
}

export interface Organization {
    id: string;
    name: string;
    type: OrganizationType;
    logo_url?: string;
    onboarded_on: string;
    representative_id: string;
    official_contact_id?: string;
    subscription_plan_id?: string;
    status: OrganizationStatus;
    partner_id?: string;
    is_deleted?: boolean;
    created_at: string;
    updated_at: string;
}

export interface OrganizationUser {
    organization_id: string;
    user_id: string;
    user?: User;
    role: OrganizationUserRole;
    status: OrganizationUserStatus;
    is_owner: boolean;
    joined_at: string;
    deleted_at?: string;
}

export interface OrganizationBilling {
    id: string;
    invoice_number: string;
    organization_id: string;
    billing_period: string;
    total_amount: number;
    status: OrganizationBillingStatus;
    due_date: string;
    paid_date?: string;
    stripe_payment_intent_id?: string;
    stripe_invoice_id?: string;
    created_at: string;
    updated_at: string;
}

export interface OrganizationBillingDetail {
    id: string;
    organization_billing_id: string;
    user_subscription_id: string;
    affiliation_code_id: string;
    billing_period: string;
    user_contribution_amount: number;
    organization_contribution_amount: number;
    total_amount: number;
    created_at: string;
    updated_at: string;
}

export interface OrganizationResponse {
    success: boolean;
    data: {
        items: Organization[];
        total: number;
        page: number;
        limit: number;
        totalPages: number;
    };
}

export interface OrganizationMessagePricing {
  id: string;
  organizationId: string;
  messageType: MessageType;
  costPerUnit: number;
  minUnits: number;
  maxUnits: number;
}

export interface OrganizationInvitation {
  id: string;
  organizationId: string;
  organizationName: string;
  organization?: Organization;
  email: string;
  roleId: string;
  status: InvitationStatus;
  expiresAt: Date;
  createdAt: Date;
}

export enum InvitationStatus {
  PENDING = 'PENDING',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
  EXPIRED = 'EXPIRED'
}

export interface Address {
  street: string;
  city: string;
  state: string;
  country: string;
  zipCode: string;
}

/** Partner portal linked user (via partner organizations). */
export interface PartnerLinkedUserMembership {
  id: string;
  organization_id?: string;
  organization?: {
    id: string;
    name: string;
    status?: string;
  };
  role: OrganizationUserRole | string;
  status: OrganizationUserStatus | string;
  is_owner: boolean;
  joined_at: string;
}

export interface PartnerLinkedUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  status: string;
  picture_url?: string | null;
  bio?: string | null;
  gender?: string | null;
  date_of_birth?: string | Date | null;
  enable_two_factor?: boolean;
  is_beta_user?: boolean;
  created_on?: string | Date;
  last_modified_on?: string | Date;
  userAuth?: unknown[];
  userRoles?: Array<{ id?: string; name?: string; role?: string; [key: string]: unknown }>;
  /** Present on detail responses. */
  memberships?: PartnerLinkedUserMembership[];
  catalysts?: CatalystSummary[];
}

export interface CatalystSummary {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

export interface PartnerLinkedUserListResponse {
  items: PartnerLinkedUser[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface PartnerLinkedUserFilters {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  role?: string;
  organizationId?: string;
  orgRole?: string;
  membershipStatus?: string;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

/** @deprecated Use PartnerLinkedUser */
export type PartnerOrgUser = PartnerLinkedUser;
/** @deprecated Use PartnerLinkedUserFilters */
export type PartnerOrgUserFilters = PartnerLinkedUserFilters;
/** @deprecated Use PartnerLinkedUserListResponse */
export type PartnerOrgUserListResponse = PartnerLinkedUserListResponse;
